import { useCallback, useMemo, useState } from 'react';
import type {
  AccountListFilterSelection,
  AccountStatus,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';

interface InternalFilterState {
  selectedGroups: Set<string>;
  selectedStatuses: Set<AccountStatus>;
}

export function useAccountListFilterSelection(): AccountListFilterSelection {
  const [filters, setFilters] = useState<InternalFilterState>({
    selectedGroups: new Set<string>(),
    selectedStatuses: new Set<AccountStatus>(),
  });

  const hasActiveFilters =
    filters.selectedStatuses.size > 0 || filters.selectedGroups.size > 0;

  const clearFilters = useCallback(() => {
    setFilters({
      selectedGroups: new Set(),
      selectedStatuses: new Set(),
    });
  }, []);

  const toggleStatus = useCallback((status: AccountStatus) => {
    setFilters((prev) => {
      const next = new Set(prev.selectedStatuses);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return { ...prev, selectedStatuses: next };
    });
  }, []);

  const toggleGroup = useCallback((uuid: string) => {
    setFilters((prev) => {
      const next = new Set(prev.selectedGroups);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return { ...prev, selectedGroups: next };
    });
  }, []);

  return useMemo<AccountListFilterSelection>(
    () => ({
      clearFilters,
      hasActiveFilters,
      selectedGroups: filters.selectedGroups,
      selectedStatuses: filters.selectedStatuses,
      toggleGroup,
      toggleStatus,
    }),
    [
      clearFilters,
      filters.selectedGroups,
      filters.selectedStatuses,
      hasActiveFilters,
      toggleGroup,
      toggleStatus,
    ],
  );
}
